/**
 * Nikshay Payload Transformer - CORRECTED VERSION
 * Transforms form values to API payload structure with all required fields
 * 
 * FIXES:
 * 1. Added missing TypeOfCase field
 * 2. TestSampleDetails now always returns sample structure with null values (never empty array)
 */

/**
 * Transforms form values to the complete Nikshay API payload structure
 * @param {Object} formValues - Formik form values
 * @param {string} patientId - Patient ID to include in payload
 * @returns {Object} Complete API payload structure
 */
export const transformToNikshayPayload = (formValues, patientId = "", userId = null) => {
  const addedBy = userId !== null && userId !== undefined ? String(userId) : null;

  // Helper function to safely get form value or return null
  const getValue = (fieldName, defaultValue = null) => {
    const value = formValues[fieldName];
    return (value === "" || value === undefined) ? defaultValue : value;
  };

  // Helper function to safely sanitize values (convert empty string/undefined to null)
  const sanitizeValue = (value, defaultValue = null) => {
    return (value === "" || value === undefined || value === null) ? defaultValue : value;
  };

  // Helper function to extract value from nested objects (handles form structure)
  const extractObjectValue = (obj) => {
    if (!obj) return null;
    
    // If it's already a simple value, return it
    if (typeof obj === 'string' || typeof obj === 'number') {
      return obj;
    }
    
    // Handle deeply nested Value objects (like {Value: {Value: "Odisha", Key: 5}})
    if (obj && obj.Value && typeof obj.Value === 'object' && obj.Value.Value !== undefined && obj.Value.Value !== null) {
      return obj.Value.Value;
    }
    
    // Handle single level Value objects (like {Value: "Odisha", Key: 5})
    if (obj && obj.Value !== undefined && obj.Value !== null) {
      return obj.Value;
    }
    
    return obj;
  };

  // Helper function to extract key from nested objects  
  const extractObjectKey = (obj) => {
    if (!obj) return null;
    
    let key = null;
    
    // If it's already a simple value, return it
    if (typeof obj === 'string' || typeof obj === 'number') {
      key = obj;
    }
    // Handle deeply nested Value objects (like {Value: {Value: "Odisha", Key: 5}})
    else if (obj && obj.Value && typeof obj.Value === 'object' && obj.Value.Key !== undefined) {
      key = obj.Value.Key;
    }
    // Handle single level Key objects (like {Value: "Odisha", Key: 5})
    else if (obj && obj.Key !== undefined) {
      key = obj.Key;
    }
    
    // Convert numeric strings to actual numbers (e.g. "27" -> 27)
    if (key !== null && key !== undefined && key !== "") {
      const trimmed = typeof key === 'string' ? key.trim() : key;
      if (trimmed !== "" && !isNaN(trimmed)) {
        const num = Number(trimmed);
        if (!isNaN(num)) {
          return num;
        }
      }
    }
    
    return key;
  };

  // Helper function to transform state/district objects to match API format (exactly 3 keys: Value, Key, Level)
  const transformStateDistrictObject = (valueObj) => {
    const value = extractObjectValue(valueObj);
    const key = extractObjectKey(valueObj);
    
    if (!value) return null;
    
    const finalLevel = (valueObj && typeof valueObj === 'object')
      ? (valueObj.Level !== undefined && valueObj.Level !== null ? valueObj.Level : (valueObj.level !== undefined && valueObj.level !== null ? valueObj.level : null))
      : null;
      
    return {
      Value: value,
      Key: key,
      Level: finalLevel !== null ? Number(finalLevel) : null
    };
  };

  // Helper function to transform facility/site objects to match API format (exactly 2 keys: Value, Key)
  const transformFacilitySiteObject = (valueObj) => {
    const value = extractObjectValue(valueObj);
    const key = extractObjectKey(valueObj);
    
    if (!value) return null;
    
    return {
      Value: value,
      Key: key
    };
  };

  // Build TestSampleDetails array - ALWAYS return sample structure with null values, never empty array
  const buildTestSampleDetails = () => {
    // Always return the sample structure even if SampleAvailability is "Absent"
    // This matches the official API payload structure
    const defaultSampleStructure = {
      SampleMappingOption: "Add New Sample",
      SampleSpecimenType: "Sputum", // Changed from null to "Sputum" to provide minimum required specimen type
      SampleSpecimenTypeIfOther: null,
      SampleSputumCollectionDetail: null,
      SampleDescription: null,
      SampleCollectionDate: null,
      SampleCollectionTime: null,
      SampleCollectionSiteState: null,
      SampleCollectionSiteDistrict: null,
      SampleCollectionSite: null,
      SampleReferralDate: null,
      SampleReferralSiteState: null,
      SampleReferralSiteDistrict: null,
      SampleReferralSite: null,
      SampleSerialId: null,
      SampleQrCode: null,
      MappedSampleId: null,
      MappedSampleSpecimenType: null,
      MappedSampleSpecimenTypeIfOther: null,
      MappedSampleSputumCollectionDetail: null,
      MappedSampleDescription: null,
      MappedSampleCollectionDate: null,
      MappedSampleCollectionTime: null,
      MappedSampleCollectionSiteState: null,
      MappedSampleCollectionSiteDistrict: null,
      MappedSampleCollectionSite: null,
      MappedSampleReferralDate: null,
      MappedSampleReferralSiteState: null,
      MappedSampleReferralSiteDistrict: null,
      MappedSampleReferralSite: null,
      MappedSampleSerialId: null,
      MappedSampleQrCode: null
    };

    // If SampleAvailability is not "Present", return default structure with nulls
    if (getValue("SampleAvailability") !== "Present") {
      return [defaultSampleStructure];
    }

    // Get the first sample from TestSampleDetails array
    const sampleArray = formValues.TestSampleDetails;
    if (!sampleArray || sampleArray.length === 0) {
      return [defaultSampleStructure];
    }
    
    return sampleArray.map((sample) => ({
      SampleMappingOption: sample.sampleMappingOption || "Add New Sample",
      SampleSpecimenType: sanitizeValue(sample.sampleType),
      SampleSpecimenTypeIfOther: sample.sampleType === "Other" ? sanitizeValue(sample.sampleTypeOther) : null,
      SampleSputumCollectionDetail: sanitizeValue(sample.sputumCollectionDetail),
      SampleDescription: sanitizeValue(sample.sampleDescription),
      SampleCollectionDate: sanitizeValue(sample.collectionDate),
      SampleCollectionTime: sanitizeValue(sample.collectionTime),
      
      // Collection Site Location Objects (State/District: 3 keys, Site: 2 keys)
      SampleCollectionSiteState: transformStateDistrictObject({
        Value: sample.collectionSiteState,
        Key: sample.collectionSiteStateKey,
        Level: sample.collectionSiteStateLevel
      }),
      SampleCollectionSiteDistrict: transformStateDistrictObject({
        Value: sample.collectionSiteDistrict, 
        Key: sample.collectionSiteDistrictKey,
        Level: sample.collectionSiteDistrictLevel
      }),
      SampleCollectionSite: transformFacilitySiteObject({
        Value: sample.collectionSite,
        Key: sample.collectionSiteKey
      }),
      
      SampleReferralDate: sanitizeValue(sample.referralDate),
      
      // Referral Site Location Objects (State/District: 3 keys, Site: 2 keys)
      SampleReferralSiteState: transformStateDistrictObject({
        Value: sample.referralSiteState,
        Key: sample.referralSiteStateKey,
        Level: sample.referralSiteStateLevel
      }),
      SampleReferralSiteDistrict: transformStateDistrictObject({
        Value: sample.referralSiteDistrict,
        Key: sample.referralSiteDistrictKey,
        Level: sample.referralSiteDistrictLevel
      }),
      SampleReferralSite: transformFacilitySiteObject({
        Value: sample.referralSite,
        Key: sample.referralSiteKey
      }),
      
      SampleSerialId: sanitizeValue(sample.sampleSerialId),
      SampleQrCode: null,
      
      // Mapped sample fields (for "Map Existing Sample" option)
      MappedSampleId: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleId) : null,
      MappedSampleSpecimenType: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleSpecimenType) : null,
      MappedSampleSpecimenTypeIfOther: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleSpecimenTypeIfOther) : null,
      MappedSampleSputumCollectionDetail: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleSputumCollectionDetail) : null,
      MappedSampleDescription: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleDescription) : null,
      MappedSampleCollectionDate: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleCollectionDate) : null,
      MappedSampleCollectionTime: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleCollectionTime) : null,
      MappedSampleCollectionSiteState: sample.sampleMappingOption === "Map Existing Sample" ? transformStateDistrictObject({
        Value: sample.mappedSampleCollectionSiteState,
        Key: sample.mappedSampleCollectionSiteStateKey,
        Level: sample.mappedSampleCollectionSiteStateLevel
      }) : null,
      MappedSampleCollectionSiteDistrict: sample.sampleMappingOption === "Map Existing Sample" ? transformStateDistrictObject({
        Value: sample.mappedSampleCollectionSiteDistrict,
        Key: sample.mappedSampleCollectionSiteDistrictKey,
        Level: sample.mappedSampleCollectionSiteDistrictLevel
      }) : null,
      MappedSampleCollectionSite: sample.sampleMappingOption === "Map Existing Sample" ? transformFacilitySiteObject({
        Value: sample.mappedSampleCollectionSite,
        Key: sample.mappedSampleCollectionSiteKey
      }) : null,
      MappedSampleReferralDate: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleReferralDate) : null,
      MappedSampleReferralSiteState: sample.sampleMappingOption === "Map Existing Sample" ? transformStateDistrictObject({
        Value: sample.mappedSampleReferralSiteState,
        Key: sample.mappedSampleReferralSiteStateKey,
        Level: sample.mappedSampleReferralSiteStateLevel
      }) : null,
      MappedSampleReferralSiteDistrict: sample.sampleMappingOption === "Map Existing Sample" ? transformStateDistrictObject({
        Value: sample.mappedSampleReferralSiteDistrict,
        Key: sample.mappedSampleReferralSiteDistrictKey,
        Level: sample.mappedSampleReferralSiteDistrictLevel
      }) : null,
      MappedSampleReferralSite: sample.sampleMappingOption === "Map Existing Sample" ? transformFacilitySiteObject({
        Value: sample.mappedSampleReferralSite,
        Key: sample.mappedSampleReferralSiteKey
      }) : null,
      MappedSampleSerialId: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleSerialId) : null,
      MappedSampleQrCode: sample.sampleMappingOption === "Map Existing Sample" ? sanitizeValue(sample.mappedSampleQrCode) : null
    }));
  };

  // Build the complete payload structure
  const payload = {
    AddedBy: addedBy,
    // Basic Test Information
    ReasonForTesting: getValue("ReasonForTesting"),
    PredominantSymptom: getValue("PredominantSymptom"),
    PredominantSymptomDuration: getValue("PredominantSymptomDuration") !== null ? String(getValue("PredominantSymptomDuration")) : null,
    HCPVisits: getValue("HCPVisits") !== null ? String(getValue("HCPVisits")) : null,
    TypeOfCase: getValue("TypeOfCase"),
    MonthsOfTreatment: getValue("MonthsOfTreatment") !== null ? String(getValue("MonthsOfTreatment")) : null,
    MonthsSinceEpisode: getValue("MonthsSinceEpisode") !== null ? String(getValue("MonthsSinceEpisode")) : null,
    
    // Diagnosis Information
    DiagnosisOfTB: Array.isArray(formValues.DiagnosisOfTB) ? formValues.DiagnosisOfTB : [],
    FollowUpReasonDSTB: getValue("FollowUpReasonDSTB"),
    TypeOfPresumptiveDRTB: getValue("TypeOfPresumptiveDRTB") || [],
    CulturePositiveMonth: getValue("CulturePositiveMonth") !== null ? String(getValue("CulturePositiveMonth")) : null,
    FollowUpReasonDRTB: getValue("FollowUpReasonDRTB"),
    FollowUpReasonDRTBMonth: getValue("FollowUpReasonDRTBMonth"),
    
    // Test Details with proper location objects - Extract from nested structure
    TestType: getValue("TestType"),
    TestingFacilityState: transformStateDistrictObject(formValues.TestingFacilityState),
    TestingFacilityDistrict: transformStateDistrictObject(formValues.TestingFacilityDistrict),
    TestingFacility: transformFacilitySiteObject(formValues.TestingFacility),
    
    // Sample and Result Availability
    SampleAvailability: getValue("SampleAvailability") || "Absent", 
    ResultAvailability: getValue("ResultAvailability") || "Absent",
    
    // Sample Details - FIXED: Now always returns sample structure with nulls, never empty array
    TestSampleDetails: buildTestSampleDetails(),
    
    // Lab Information
    LabSerialNumber: getValue("LabSerialNumber"),
    ResultSampleId: getValue("ResultSampleId") ? {
      Key: String(getValue("ResultSampleId")),
      Value: `Sample-${getValue("ResultSampleId")}`,
      Label: `Sample-${getValue("ResultSampleId")}`,
      ValueKey: `Sample${getValue("ResultSampleId")}`
    } : null,
    IndurationSize: getValue("IndurationSize"),
    ResultDateTested: getValue("ResultDateTested"),
    ResultDateReported: getValue("ResultDateReported"),
    ResultReportedBy: getValue("ResultReportedBy"),
    
    // FLLPA Fields - Add Mutation Details Control
    FLLPA_AddMutationDetails: getValue("ResultAvailability") === "Present" 
      ? getValue("FLLPA_AddMutationDetails") 
      : null,
    SLLPA_AddMutationDetails: getValue("ResultAvailability") === "Present" 
      ? getValue("SLLPA_AddMutationDetails") 
      : null,
    
    // FLLPA Mutation Details - ALL FIELDS with null defaults
    FLLPASample: getValue("FLLPASample"),
    FLLPA_TUB_BAND: getValue("FLLPA_TUB_BAND"),
    FLLPA_Rpob_Locus_Control: getValue("FLLPA_Rpob_Locus_Control"),
    FLLPA_RpoB_WT1: getValue("FLLPA_RpoB_WT1"),
    FLLPA_RpoB_WT2: getValue("FLLPA_RpoB_WT2"),
    FLLPA_WT3: getValue("FLLPA_WT3"),
    FLLPA_WT4: getValue("FLLPA_WT4"),
    FLLPA_WT5: getValue("FLLPA_WT5"),
    FLLPA_WT6: getValue("FLLPA_WT6"),
    FLLPA_WT7: getValue("FLLPA_WT7"),
    FLLPA_WT8: getValue("FLLPA_WT8"),
    FLLPA_MUT1_D516V: getValue("FLLPA_MUT1_D516V"),
    FLLPA_MUT2A_H526Y: getValue("FLLPA_MUT2A_H526Y"),
    FLLPA_MUT2B_H526D: getValue("FLLPA_MUT2B_H526D"),
    FLLPA_MUT3_S513L: getValue("FLLPA_MUT3_S513L"),
    FLLPA_KAT_G: getValue("FLLPA_KAT_G"),
    FLLPA_KAT_WT1: getValue("FLLPA_KAT_WT1"),
    FLLPA_KAT_MUT1: getValue("FLLPA_KAT_MUT1"),
    FLLPA_KAT_MUT2: getValue("FLLPA_KAT_MUT2"),
    FLLPA_INH_A: getValue("FLLPA_INH_A"),
    FLLPA_INH_WT1: getValue("FLLPA_INH_WT1"),
    FLLPA_INH_WT2: getValue("FLLPA_INH_WT2"),
    FLLPA_INH_MUT1: getValue("FLLPA_INH_MUT1"),
    FLLPA_INH_MUT2: getValue("FLLPA_INH_MUT2"),
    FLLPA_MUT3A: getValue("FLLPA_MUT3A"),
    FLLPA_MUT3B: getValue("FLLPA_MUT3B"),
    
    // SLLPA Mutation Details (ALL fields from API payload)
    GYR_A_Locus_Control: getValue("GYR_A_Locus_Control"),
    SLLPA_WT1_85_90: getValue("SLLPA_WT1_85_90"),
    SLLPA_WT2_89_93: getValue("SLLPA_WT2_89_93"),
    SLLPA_WT3_92_97: getValue("SLLPA_WT3_92_97"),
    SLLPA_MUT1_A90V: getValue("SLLPA_MUT1_A90V"),
    SLLPA_MUT2_S91P: getValue("SLLPA_MUT2_S91P"),
    SLLPA_MUT3A_D94A: getValue("SLLPA_MUT3A_D94A"),
    SLLPA_MUT3B_D94N: getValue("SLLPA_MUT3B_D94N"),
    SLLPA_MUT3C_D94G: getValue("SLLPA_MUT3C_D94G"),
    SLLPA_MUT3D_D94H: getValue("SLLPA_MUT3D_D94H"),
    GYR_B_Locus_Control: getValue("GYR_B_Locus_Control"),
    SLLPA_WT1_536_541: getValue("SLLPA_WT1_536_541"),
    SLLPA_MUT1_N538D: getValue("SLLPA_MUT1_N538D"),
    SLLPA_MUT2_E540V: getValue("SLLPA_MUT2_E540V"),
    RRS_Locus_Control: getValue("RRS_Locus_Control"),
    SLLPA_WT1_1401_02: getValue("SLLPA_WT1_1401_02"),
    SLLPA_WT2_1484: getValue("SLLPA_WT2_1484"),
    SLLPA_MUT1_A1401G: getValue("SLLPA_MUT1_A1401G"),
    SLLPA_MUT2_G148T: getValue("SLLPA_MUT2_G148T"),
    EIS_Locus_Control: getValue("EIS_Locus_Control"),
    SLLPA_WT1_37: getValue("SLLPA_WT1_37"),
    SLLPA_WT2_14_12_10: getValue("SLLPA_WT2_14_12_10"),
    SLLPA_WT3_2: getValue("SLLPA_WT3_2"),
    SLLPA_MUT1_C_14T: getValue("SLLPA_MUT1_C_14T"),
    
    // Text Result and Other Test Results
    TextResult: getValue("TextResult"),
    CultureType: getValue("CultureType"),
    DSTToDrug: getValue("DSTToDrug"),
    ResistanceStatus: getValue("ResistanceStatus"),
    
    // Results
    FLLPA_Result: getValue("FLLPA_Result"),
    SLLPA_Result: getValue("SLLPA_Result"),
    PathodetectResult: getValue("PathodetectResult"),
    IndurationSize: getValue("IndurationSize"),
    
    // Resistance Results
    ResistanceToRifampicin: getValue("ResistanceToRifampicin"),
    HighLevelResistanceToIsoniazid: getValue("HighLevelResistanceToIsoniazid"),
    LowLevelResistanceToIsoniazid: getValue("LowLevelResistanceToIsoniazid"),
    ResistanceToFluoroquinolones: getValue("ResistanceToFluoroquinolones"),
    ResistanceToSecondLineInjectableDrugs: getValue("ResistanceToSecondLineInjectableDrugs"),
    ResistanceToSecondLineInjectableDrugsLowLevel: getValue("ResistanceToSecondLineInjectableDrugsLowLevel"),
    
    // Final Results
    FinalInterpretation: getValue("FinalInterpretation"),
    Remarks: getValue("Remarks"),
    
    // Patient ID
    PatientId: String(patientId || getValue("PatientId") || "")
  };

  return payload;
};

/**
 * Helper function to validate required fields based on test type and conditions
 * @param {Object} formValues - Formik form values
 * @returns {Object} Validation result with isValid flag and errors array
 */
export const validateNikshayForm = (formValues) => {
  const errors = [];
  
  // Basic required fields
  if (!formValues.ReasonForTesting) {
    errors.push("Reason for Testing is required");
  }
  
  if (!formValues.TestType) {
    errors.push("Test Type is required");
  }
  
  // TypeOfCase validation for diagnosis scenarios
  if ((formValues.ReasonForTesting === "Diagnosis of TB" || formValues.ReasonForTesting === "Diagnosis of DRTB") && !formValues.TypeOfCase) {
    errors.push("Type of Case is required for diagnosis scenarios");
  }
  
  // Conditional validation based on ResultAvailability
  if (formValues.ResultAvailability === "Present") {
    if (!formValues.ResultDateReported) {
      errors.push("Date Reported is required when Result is Present");
    }
    
    if (!formValues.FinalInterpretation) {
      errors.push("Final Interpretation is required when Result is Present");
    }
  }
  
  // Sample validation
  if (formValues.SampleAvailability === "Present") {
    const sampleArray = formValues.TestSampleDetails;
    if (!sampleArray || sampleArray.length === 0) {
      errors.push("Sample details are required when Sample is Present");
    } else {
      const firstSample = sampleArray[0];
      if (!firstSample.sampleMappingOption) {
        errors.push("Sample Mapping Option is required when Sample is Present");
      }
      
      if (firstSample.sampleMappingOption === "Add New Sample") {
        if (!firstSample.sampleType) {
          errors.push("Sample Type is required for new samples");
        }
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Example usage in form submission
 */
export const handleFormSubmission = async (formValues, apiClient, patientId) => {
  try {
    // Validate form
    const validation = validateNikshayForm(formValues);
    if (!validation.isValid) {
      console.error("Form validation failed:", validation.errors);
      return { success: false, errors: validation.errors };
    }
    
    // Transform to API payload
    const payload = transformToNikshayPayload(formValues, patientId);
    
    // Log payload for debugging
    console.log("Transformed Payload:", JSON.stringify(payload, null, 2));
    
    // Make API call (replace with your actual API endpoint)
    const response = await apiClient.post('/nikshay/test', payload);
    
    return { success: true, data: response.data };
    
  } catch (error) {
    console.error("Form submission failed:", error);
    return { success: false, error: error.message };
  }
};